import React from "react";
import { Tag } from "antd";
import { formatCurrencyWithConversion } from "../../../../utils/currencyUtils";

/**
 * Generate credit columns for the credit allocation table
 * @param {Object} allocationStatus - Current allocation status information
 * @returns {Array} Array of column definitions
 */
export const getCreditColumns = (allocationStatus) => {
  return [
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Issue Date",
      dataIndex: "issue_date",
      key: "issue_date",
      width: 100,
      render: (date) => (date ? new Date(date).toLocaleDateString() : ""),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Type",
      key: "credit_type",
      width: 100,
      render: (_, record) => {
        // credit_type: ACCPAY = vendor credit, ACCREC = customer credit
        // transaction_detail or detail may also indicate DEBIT/CREDIT
        const detail = record.transaction_detail || record.detail;
        const creditType = record.credit_type;

        // If we have explicit DEBIT/CREDIT detail, use it
        if (detail === "DEBIT") {
          return <Tag color="red">Debit</Tag>;
        } else if (detail === "CREDIT") {
          return <Tag color="green">Credit</Tag>;
        }

        // Fallback: use credit_type to determine
        // ACCPAY = Accounts Payable (vendor credit = you get credit FROM vendor)
        // ACCREC = Accounts Receivable (customer credit = you give credit TO customer)
        if (creditType === "ACCPAY") {
          return <Tag color="blue">Vendor Credit</Tag>;
        } else if (creditType === "ACCREC") {
          return <Tag color="purple">Customer Credit</Tag>;
        }

        return <Tag>{creditType || "-"}</Tag>;
      },
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 120,
      render: (amount, record) => {
        const currency =
          record?.currency || record?.originalData?.currency || "$";
        return formatCurrencyWithConversion(amount || 0, currency);
      },
    },
    {
      title: "Allocated",
      key: "allocated",
      width: 140,
      render: (_, record) => {
        if (allocationStatus) {
          const currency =
            record?.currency || record?.originalData?.currency || "$";
          // Don't round to 100% unless it's actually fully allocated
          let displayPercent = allocationStatus.percentAllocated;
          if (displayPercent >= 99.995 && displayPercent < 100) {
            displayPercent = 99.99; // Cap at 99.99% if not actually 100%
          }
          return (
            <span>
              {formatCurrencyWithConversion(
                allocationStatus.totalAllocated || 0,
                currency
              )}{" "}
              ({displayPercent.toFixed(2)}%)
            </span>
          );
        }
        return "0.00 (0%)";
      },
    },
    {
      title: "Remaining",
      key: "unallocated",
      width: 120,
      render: (record) => {
        const currency =
          record?.currency || record?.originalData?.currency || "$";
        if (allocationStatus && record.total_amount) {
          const totalAmount = parseFloat(record.total_amount);
          const unallocatedAmount =
            totalAmount - allocationStatus.totalAllocated;
          return formatCurrencyWithConversion(unallocatedAmount, currency);
        }
        return formatCurrencyWithConversion(record.total_amount || 0, currency);
      },
    },
    {
      title: "Status",
      key: "allocation_status",
      width: 130,
      render: () => {
        if (allocationStatus) {
          return (
            <Tag
              color={
                allocationStatus.color === "success"
                  ? "green"
                  : allocationStatus.color === "warning"
                  ? "orange"
                  : "default"
              }
            >
              {allocationStatus.label}
            </Tag>
          );
        }
        return <Tag color="default">Unallocated</Tag>;
      },
    },
  ];
};

export default getCreditColumns;
